<template>
  <!--
    A CASA (redesign 03/09). A versão antiga era dois vidros borrados sobre o shop-interior
    esticado, com uma casinha DESENHADA em CSS (cama, lua, abajur) para o estado de descanso —
    contra a regra "arte se recorta, não se desenha". Agora é a linguagem do jogo: cena calma de
    água doce, as casas como QUADROS de papel na parede (papel-quadro, D15), e o descanso é um
    bilhete que respira. A parede de troféus do D29 entra aqui quando houver backend.
  -->
  <FishScene cena="agua-doce" :escurecimento="0.45" class="casa">
    <div class="casa__interior">
      <div class="casa__topo">
        <h1 class="casa__titulo banner-titulo">{{ $t('house.title') }}</h1>
      </div>

      <div v-if="loading" class="casa__carregando papel-empilhado">
        <p>{{ $t('house.loading') }}</p>
        <span class="casa__pontos" aria-hidden="true"><i></i><i></i><i></i></span>
      </div>

      <div v-else class="casa__salas">
        <!-- A parede das casas: cada uma é um quadro pendurado. -->
        <section class="casa__parede">
          <ul class="casa__quadros">
            <li
              v-for="house in houses"
              :key="house.id"
              class="casa__quadro papel-quadro"
              :class="{ 'casa__quadro--morando': isHouseEquipped(house) }"
            >
              <img class="casa__retrato suavizada" :src="imagemDa(house)" :alt="house.name" />
              <p class="casa__nome">{{ house.name }}</p>
              <p class="casa__bonus t-numero">
                <FishIcon nome="atributo-energia" tamanho="miudo" />
                +{{ house.rechargeStamina || 0 }} {{ $t('house.perInterval') }}
              </p>
              <FishButton
                tamanho="pequeno"
                bloco
                :variante="isHouseEquipped(house) ? 'neutro' : 'primario'"
                :desabilitado="isHouseEquipped(house)"
                @click="equipHouse(house)"
              >
                {{ isHouseEquipped(house) ? $t('house.equipButton.equipped') : $t('house.equipButton.equip') }}
              </FishButton>
            </li>
          </ul>
          <p v-if="!houses.length" class="casa__semcasa papel-empilhado">
            {{ $t('house.title') }} · 0
          </p>
        </section>

        <!-- O quarto: a energia, a conta da recarga, e a ação. -->
        <section class="casa__quarto">
          <div class="casa__papel">
            <FishMeter
              :valor="stamina.current"
              :maximo="stamina.max"
              cor="agua"
              :rotulo="$t('upgrade.stamina')"
              mostrar-numero
            />

            <ul class="casa__conta">
              <li>
                <span>{{ $t('house.recharge.baseRate') }}</span>
                <strong class="t-numero">{{ baseRechargeRate }}</strong>
              </li>
              <li v-if="getEquippedHouseData">
                <span>{{ $t('house.recharge.houseBonus') }}</span>
                <strong class="t-numero">+{{ getEquippedHouseData.rechargeStamina }}</strong>
              </li>
              <li class="casa__conta-total">
                <span>{{ $t('house.recharge.total') }}</span>
                <strong class="t-numero">{{ totalRechargeRate }} {{ $t('house.perInterval') }}</strong>
              </li>
            </ul>

            <!-- Sem `.acao-principal` aqui: o degradê escuro dela é para CTA fixo sobre CENÁRIO;
                 dentro do papel ele vira uma mancha (o autor apontou). -->
            <div>
              <FishButton
                :variante="isResting ? 'neutro' : 'primario'"
                tamanho="grande"
                bloco
                :som="isResting ? 'clique' : 'recompensa'"
                @click="toggleRest"
              >
                {{ isResting ? $t('house.rest.stop') : $t('house.rest.start') }}
              </FishButton>
            </div>

            <!-- Descansando: um bilhete que respira devagar — o D0 em uma linha. -->
            <p v-if="isResting" class="casa__sono">
              <span class="casa__zzz" aria-hidden="true">Z z z</span>
              +{{ totalRechargeRate }} {{ $t('house.perInterval') }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </FishScene>
</template>

<script>
import { mapState } from 'vuex';
import { FishScene, FishButton, FishIcon, FishMeter } from '@/shared/ui';
import { getItemImage } from '@/shared/utils/itemImages';

export default {
  name: 'HouseRest',
  components: {
    FishScene,
    FishButton,
    FishIcon,
    FishMeter
  },
  computed: {
    ...mapState({
      stamina: state => state.player.stamina,
      houses: state => state.player.inventory.houses || [],
      equipment: state => state.player.equipment,
      houseList: state => state.shop.houses || []
    }),
    equippedHouse() {
      return this.equipment?.house || null;
    },
    getEquippedHouseData() {
      // A peça do baú do V2 já traz `rechargeStamina` e `equipped`; a versão
      // antiga procurava na loja comparando um objeto com um nome — sempre falso.
      return this.houses.find(house => house.equipped) || null;
    },
    isDataReady() {
      return this.houseList?.length > 0;
    },
    totalRechargeRate() {
      return this.baseRechargeRate + (this.getEquippedHouseData?.rechargeStamina || 0);
    }
  },
  data() {
    return {
      isResting: false,
      baseRechargeRate: 1,
      canRest: true,
      loading: true
    }
  },
  methods: {
    imagemDa(house) {
      return house.image || getItemImage(house.name);
    },
    async toggleRest() {
      if (!this.canRest) {
        return;
      }
      try {
        this.isResting = !this.isResting;
        await this.$store.dispatch('player/updateIsHouse', this.isResting);
      } catch (error) {
        console.error('Failed to toggle rest state:', error);
        this.isResting = false;
      }
    },
    isHouseEquipped(house) {
      return house.equipped === true;
    },
    async equipHouse(house) {
      try {
        await this.$store.dispatch('player/equipHouse', { houseId: house.id });
      } catch (error) {
        console.error('Failed to equip house:', error);
      }
    },
    async initializeHouseData() {
      this.loading = true;
      try {
        const [isHouse, noRestEvent] = await Promise.all([
          this.$store.dispatch('player/getIsHouse', this.$store.state.player.id),
          this.$store.dispatch('events/checkNoRestEvent'),
          this.$store.dispatch('shop/fetchShopItems')
        ]);

        this.isResting = isHouse?.data?.isHouse || false;
        this.canRest = !noRestEvent?.hasNoRest;
        this.loading = false;
      } catch (error) {
        console.error('Failed to initialize house rest:', error);
        this.isResting = false;
        this.canRest = true;
        this.loading = false;
      }
    }
  },
  async mounted() {
    await this.initializeHouseData();
  },
  watch: {
    isDataReady: {
      immediate: true,
      async handler(ready) {
        if (ready) {
          await this.initializeHouseData();
        }
      }
    }
  }
}
</script>

<style scoped>
.casa {
  min-height: 100vh;
}

.casa__interior {
  max-width: 880px;
  margin: 0 auto;
  padding: var(--e-4) var(--e-3) calc(var(--altura-barra, 104px) + var(--e-5));
}

.casa__topo {
  text-align: center;
  margin-bottom: var(--e-4);
}

.casa__titulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-5);
}

.casa__carregando {
  --folha: 360px;
  width: min(360px, 100%);
  margin: 0 auto;
  text-align: center;
  font-size: var(--f-2);
}

.casa__carregando p {
  margin: 0 0 var(--e-1);
}

.casa__pontos i {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin: 0 3px;
  background: var(--tinta-fraca);
  animation: casa-respirar 1.4s ease-in-out infinite;
}

.casa__pontos i:nth-child(2) { animation-delay: 0.2s; }
.casa__pontos i:nth-child(3) { animation-delay: 0.4s; }

.casa__salas {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--e-4);
}

@media (min-width: 900px) {
  .casa__salas {
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
}

/* ------------------------------------------------------------------------------------------
 * A parede: os quadros das casas. `papel-quadro` é a moldura de madeira sobre papel do pack.
 * ---------------------------------------------------------------------------------------- */
.casa__quadros {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  /* Quadro tem tamanho de quadro: com uma casa só, um `1fr` esticaria a nota na coluna toda. */
  grid-template-columns: repeat(auto-fill, minmax(180px, 220px));
  justify-content: center;
  gap: var(--e-3);
}

.casa__quadro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  text-align: center;
  transition: transform var(--t-curto) var(--curva-suave);
}

@media (hover: hover) {
  .casa__quadro:hover {
    transform: translateY(-3px) rotate(-0.5deg);
  }
}

/* A casa onde se mora: o quadro pende ligeiramente torto, como quadro de casa de verdade. */
.casa__quadro--morando {
  transform: rotate(0.6deg);
  outline: 2px solid var(--latao);
  outline-offset: -6px;
}

.casa__retrato {
  width: 96px;
  height: 96px;
  object-fit: contain;
}

.casa__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.casa__bonus {
  margin: 0 0 var(--e-1);
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.casa__semcasa {
  --folha: 320px;
  width: min(320px, 100%);
  margin: 0 auto;
  text-align: center;
  color: var(--tinta-fraca);
}

/* ------------------------------------------------------------------------------------------
 * O quarto: papel simples, a conta da recarga como recibo, e a ação grande.
 * ---------------------------------------------------------------------------------------- */
.casa__papel {
  background: var(--papel);
  color: var(--tinta);
  padding: var(--e-4);
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

.casa__conta {
  list-style: none;
  margin: 0;
  padding: var(--e-2) 0;
  border-top: 2px dashed var(--papel-sombra);
  border-bottom: 2px dashed var(--papel-sombra);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.casa__conta li {
  display: flex;
  justify-content: space-between;
  gap: var(--e-3);
}

.casa__conta strong {
  color: var(--tinta);
}

.casa__conta-total strong {
  color: var(--latao);
  font-size: var(--f-3);
}

.casa__sono {
  margin: 0;
  text-align: center;
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.casa__zzz {
  display: block;
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  color: var(--tinta-lapis, var(--tinta-fraca));
  letter-spacing: 4px;
  animation: casa-respirar 3s ease-in-out infinite;
}

@keyframes casa-respirar {
  0%, 100% { opacity: 0.45; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-2px); }
}

@media (prefers-reduced-motion: reduce) {
  .casa__pontos i,
  .casa__zzz {
    animation: none;
  }
}
</style>
